/**
 * Activity's Calendar — `docs/activity.md` §6 (Boards 2–6, 18).
 *
 * The side panel (desktop), the toolbar, and one of: the hours grid for one
 * person (days as columns), Day side by side (a column per person), the
 * person-by-week rows (Work week and Week side by side), Combined (one grid,
 * colour is the person, overlaps in lanes, *Free for all*), or Month. Every
 * number on it — drive minutes, the day's driving, free time, the forecast —
 * is the server's; this draws them (rule 2).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ApiError, arrowDay, monthDay, rangeLabel, stepView, toneOf, viewDays, weekdayShort, zonedInstant,
  type ActivityDay, type CalendarDrive, type ActivityView, type CalendarAnswer, type CalendarItem, type CombinedItem,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { useActivity } from '../activity-context.js'
import { isTablet, useActivityLayout, type ActivityLayout } from '../use-layout.js'
import { Face } from '../faces.js'
import { activityRequest } from '../api.js'
import { Forecast, StopBlock, TaskChip, TimeBlock, personFill } from './blocks.js'
import { BLOCK_GAP, GRID_METRICS, span as clockSpan } from './look.js'
import { SidePanel } from './side-panel.js'
import { TimeGrid, type GridColumn } from './time-grid.js'
import { CalendarToolbar } from './toolbar.js'
import { useCalendar } from './use-calendar.js'
import { WeekRows } from './week-rows.js'
import { MonthGrid } from './month.js'
import { DayHead } from './day-head.js'
import {
  CalendarOverlays, messageAbout, openActivityNew, openItemSheet, pointAnchor, readStop,
  type CalendarAction, type StopCardAnswer,
} from './overlays.js'
import { PhoneCalendar } from './phone-calendar.js'
import { holdForMenu, holdToDrag } from './hold.js'
import { useNowMinutes } from './now.js'
import { openNewRequest } from '../../requests/open-request.js'
import { asksWhich, dropRefusal, movedStart, occurrenceOf, openingMinutes, pressPart, resized, type DropTarget } from './drag.js'
import { CalendarMenu, isMenuKey, itemMenu, menuAtElement, type MenuAt } from './item-menu.js'
import { MoveCard, TellPrompt, WhichQuestion, type WhichVerb } from './actions.js'
import { ACTIVITY_MOVED_EVENT, type ActivityMovedDetail } from './tell.js'
import { markDone } from './done.js'
import { writeMove as writeItemMove, type MovePlace } from './moves.js'

/**
 * The whole day is on the grid, 12 AM to 12 AM (Aaron, Oct 23: some people
 * keep everything here). It opens at the start of the working day — the
 * remembered start hour, or earlier when something on the screen is
 * (`openingMinutes`).
 */
const FULL_DAY = { start: 0, end: 24 } as const

/** The calendar column under the pointer, and the top of its hours. */
function columnAt(x: number, y: number): { key: string; top: number } | null {
  for (const element of document.elementsFromPoint(x, y)) {
    const column = (element as HTMLElement).closest?.('[data-column]')
    if (!column) continue
    const hours = column.firstElementChild as HTMLElement | null
    return { key: column.getAttribute('data-column') ?? '', top: (hours ?? column).getBoundingClientRect().top }
  }
  return null
}

/** Where a change to an item goes: a place on the grid, and for a repeat which of its occurrences (`moves.ts`). */
type Place = MovePlace

/** An item's block on the grid, for a card or a prompt to stand beside. */
function blockElement(item: Pick<CalendarItem, 'key'>): HTMLElement | null {
  const key = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(item.key) : item.key
  return document.querySelector<HTMLElement>(`[data-stop="${key}"], [data-block="${key}"], [data-task="${key}"]`)
}

/** What a failed write says, as the server worded it. */
const sentenceOf = (cause: unknown, fallback: string) =>
  cause instanceof ApiError && cause.message ? cause.message : fallback

/** Overlapping things in one column share its width (the same rule Combined uses). */
function lanesOf<T extends { start: number; end: number }>(items: T[]): (T & { lane: number; lanes: number; span: number })[] {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end)
  const out: (T & { lane: number; lanes: number; span: number })[] = []
  let cluster: (T & { lane: number; lanes: number })[] = []
  let clusterEnd = -1
  let ends: number[] = []
  const close = () => {
    const lanes = Math.max(1, ...cluster.map((c) => c.lane + 1))
    for (const c of cluster) {
      let span = 1
      for (let lane = c.lane + 1; lane < lanes; lane += 1) {
        if (cluster.some((o) => o.lane === lane && o.start < c.end && c.start < o.end)) break
        span += 1
      }
      out.push({ ...c, lanes, span })
    }
    cluster = []
    ends = []
  }
  for (const item of sorted) {
    if (cluster.length > 0 && item.start >= clusterEnd) close()
    let lane = ends.findIndex((end) => end <= item.start)
    if (lane === -1) { lane = ends.length; ends.push(item.end) } else ends[lane] = item.end
    cluster.push({ ...item, lane, lanes: 1 })
    clusterEnd = Math.max(clusterEnd, item.end)
  }
  if (cluster.length > 0) close()
  return out
}

function OffChip({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-5 items-center truncate rounded-[5px] bg-line-soft px-[7px] text-[11px] font-semibold text-text-muted">
      {children}
    </span>
  )
}

/** The phone's calendar is its own (Board 13); the desktop and the iPad share the grid (Boards 2–6, 12, 18). */
/** Controls that use the arrow keys themselves; the calendar's day arrows stand aside inside them. */
const KEYED_ROLES = ['dialog', 'menu', 'listbox', 'tab', 'tablist', 'radio', 'radiogroup', 'slider'] as const

export function CalendarTab() {
  const layout = useActivityLayout()
  return layout === 'phone' ? <PhoneCalendar /> : <GridCalendar layout={layout} />
}

function GridCalendar({ layout }: { layout: Exclude<ActivityLayout, 'phone'> }) {
  const { state, change, people, today, me, directory } = useActivity()
  const navigate = useNavigate()
  const tablet = isTablet(layout)
  const metrics = GRID_METRICS[layout]
  const scale = metrics.scale
  const several = people.length > 1
  const remembered: ActivityView = several ? state.calendar.viewSeveral : state.calendar.viewOne
  /* Work week steps aside under 1,180 px (§10.1): it reads as Week there. */
  const view: ActivityView = layout === 'portrait' && remembered === 'work_week' ? 'week' : remembered
  const mode = state.calendar.several
  const date = state.calendar.date
  const days = useMemo(() => viewDays(view, date), [view, date])
  const combined = several && mode === 'combined' && view !== 'month'
  const { answer, toBook, error, reload } = useCalendar(people, days[0]!, days[days.length - 1]!, combined)
  const [action, setAction] = useState<CalendarAction | null>(null)
  const show = state.calendar.show
  const now = useNowMinutes(answer)
  const hours = FULL_DAY
  const openAt = openingMinutes(answer, state.calendar.startHour)
  /* *Today* scrolls the hours to now as well as the board to today. */
  const [scrollTo, setScrollTo] = useState<{ minutes: number; nonce: number } | null>(null)
  /* A move the calendar will not make says why, briefly. */
  const [notice, setNotice] = useState<string | null>(null)
  useEffect(() => {
    if (!notice) return undefined
    const timer = window.setTimeout(() => setNotice(null), 4500)
    return () => window.clearTimeout(timer)
  }, [notice])

  /* Aaron, Oct 24 (notes 1.6–1.8, small Q3): the menu, the repeat's question, one occurrence's card, and telling the customer. */
  const [menu, setMenu] = useState<{
    item: CalendarItem; at: MenuAt; card: StopCardAnswer | null
    /** Opened from the stop card's ⋯: the card stays open under it, and *Open* is not offered. */
    fromCard?: boolean
  } | null>(null)
  const [which, setWhich] = useState<{
    item: CalendarItem; verb: WhichVerb; at: MenuAt; onPick: (which: 'one' | 'all') => void; onCancel: () => void
    /** Where it was dropped or what it was resized to, for the answers' words (Aaron, Oct 25, answer 2.1). */
    to?: { date: string; start: number; minutes: number }
  } | null>(null)
  const [moveCard, setMoveCard] = useState<{ item: CalendarItem; mode: 'one' | 'all' | 'plain'; at: MenuAt } | null>(null)
  const [tell, setTell] = useState<StopCardAnswer | null>(null)
  const canSend = directory ? directory.people.some((p) => p.id === me.user.id && p.inbox) : true
  const zone = answer?.timeZone ?? me.user.timeZone

  /**
   * Writes a move: a one-off by its own path (a stop's `PATCH /activities`,
   * a block's), a repeat's occurrence — just this one, or every one — by
   * `POST /activity/occurrences/:id/move` (small Q3). `moves.ts` is the one
   * writer, the phone's held finger's too (Aaron, Oct 27, note 1.1.4).
   */
  const writeMove = (item: CalendarItem, place: Place, repeat: 'one' | 'all' | null): Promise<unknown> =>
    writeItemMove(item, place, repeat, zone)

  /* After a site visit or a meeting with a customer has a new time: *Let Renee know?* (note 1.8). */
  const offerTell = (item: Pick<CalendarItem, 'id' | 'kind' | 'occurrence'>) => {
    if (item.kind !== 'appointment' && item.kind !== 'meeting') return
    void readStop(item).then((card) => { if (card.contact) setTell(card) }).catch(() => {})
  }
  useEffect(() => {
    const onMoved = (event: Event) => {
      const detail = (event as CustomEvent<ActivityMovedDetail>).detail
      if (detail?.id) offerTell({ id: detail.id, kind: detail.kind, occurrence: detail.occurrence ?? null })
    }
    window.addEventListener(ACTIVITY_MOVED_EVENT, onMoved)
    return () => window.removeEventListener(ACTIVITY_MOVED_EVENT, onMoved)
  })

  /* The menu (note 1.7): what it knows at once, and the customer's ways in once the stop card has said who. */
  /* Whose menu is open, read by the events of the same press before React has drawn it. */
  const menuFor = useRef<string | null>(null)
  const closeMenu = () => { menuFor.current = null; setMenu(null) }
  const openMenu = (item: CalendarItem, at: MenuAt, card: StopCardAnswer | null = null, fromCard = false) => {
    if (!fromCard) setAction(null)
    menuFor.current = item.key
    setMenu({ item, at, card, fromCard })
    if (card || (item.kind !== 'appointment' && item.kind !== 'meeting')) return
    void readStop(item)
      .then((found) => setMenu((was) => (was && was.item.key === item.key ? { ...was, card: found } : was)))
      .catch(() => {})
  }
  /*
   * Shift+F10 and the context-menu key open the menu from the keyboard; the
   * browser may then send a `contextmenu` of its own for the same press, which
   * finds that item's menu already open and leaves it be (a real right-click
   * closes it first, on its press). One with no point (a key's) stands by the item.
   */
  const rightClick = (item: CalendarItem) => (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    if (menuFor.current === item.key) return
    const keyed = event.clientX === 0 && event.clientY === 0
    openMenu(item, keyed ? menuAtElement(event.currentTarget) : { x: event.clientX, y: event.clientY })
  }
  const pointAt = (at: MenuAt) => ({ getBoundingClientRect: () => new DOMRect(at.x, at.y, 1, 1) })
  const openCard = (item: CalendarItem, at: MenuAt) => {
    if (item.kind === 'task') setAction({ kind: 'task', item })
    else setAction({ kind: item.kind === 'block' ? 'block' : 'stop', item, anchor: blockElement(item) ?? pointAt(at) })
  }
  const tickTo = async (item: CalendarItem, done: boolean) => {
    try {
      /* One occurrence of a repeating meeting by itself (0152). */
      await markDone(item, done)
    } catch (cause) {
      setNotice(sentenceOf(cause, `${item.title} couldn’t be ticked.`))
    }
    reload()
  }

  /**
   * *Reschedule* (the menu, the stop card): the item's own sheet with *When*
   * — folder 48's book mode, the meeting's or the task's sheet — or, for a
   * time block, which has none, a card with the day and the time. A repeat
   * asks *Just this one · Every one* first; just this one is that card.
   */
  const reschedule = (item: CalendarItem, at: MenuAt) => {
    setAction(null)
    const series = () => {
      if (item.kind === 'block') setMoveCard({ item, mode: 'all', at })
      else openItemSheet(item)
    }
    if (item.kind === 'appointment') { openItemSheet(item); return }
    if (asksWhich(item)) {
      setWhich({ item, verb: 'reschedule', at, onCancel: () => setWhich(null),
        onPick: (choice) => { setWhich(null); if (choice === 'one') setMoveCard({ item, mode: 'one', at }); else series() } })
      return
    }
    if (item.kind === 'block') setMoveCard({ item, mode: 'plain', at })
    else openItemSheet(item)
  }

  /**
   * *Edit* (the menu, the stop card's ⋯ — Aaron, Oct 25, phone note 4: "at
   * least in the three dot menu i should be able to enter an edit mode"): the
   * item's own sheet, a meeting's in edit mode and a site visit's booking, as
   * a double-click opens them. A repeating meeting asks first, as a move does:
   * *Just this one* is the meeting's whole sheet for that occurrence — its
   * day, time, title, people, place, *With*, links and notes, saved for it
   * alone (Aaron, Oct 27, answer 12: "it should be able to change pretty much
   * anything"; 0152) — and *Every one* the meeting's sheet, for the series.
   */
  const editItem = (item: CalendarItem, at: MenuAt) => {
    setAction(null)
    if (item.kind === 'meeting' && asksWhich(item)) {
      setWhich({ item, verb: 'edit', at, onCancel: () => setWhich(null),
        onPick: (choice) => {
          setWhich(null)
          if (choice === 'one') openItemSheet(item, item.jobId, occurrenceOf(item))
          else openItemSheet(item)
        } })
      return
    }
    openItem(item)
  }

  const menuItems = menu ? itemMenu(menu.item, {
    open: menu.fromCard ? undefined : () => openCard(menu.item, menu.at),
    edit: () => editItem(menu.item, menu.at),
    reschedule: () => reschedule(menu.item, menu.at),
    message: menu.card ? (channel) => messageAbout(menu.card!, channel, false) : undefined,
    tick: (done) => void tickTo(menu.item, done),
    openJob: (jobId) => navigate(`/jobs/${jobId}`),
    remove: () => setRemoving({ item: menu.item, rect: blockElement(menu.item)?.getBoundingClientRect() ?? new DOMRect(menu.at.x, menu.at.y, 1, 1) }),
  }, { contact: menu.card?.contact ?? null, canSend, done: menu.card?.done }) : []

  const setView = (next: ActivityView) => change({ calendar: several ? { viewSeveral: next } : { viewOne: next } })
  const setDate = (next: string) => change({ calendar: { date: next } })
  const weatherOf = (day: string) => answer?.dates.find((d) => d.date === day)?.weather ?? null
  const dayWeather = view === 'day' ? weatherOf(date) : null

  const visible = (item: CalendarItem) => {
    if (item.kind === 'appointment' || item.kind === 'meeting') return show.visits
    if (item.kind === 'task') return show.tasks
    return show.blocks
  }

  const tick = async (item: CalendarItem) => {
    await activityRequest(`/activities/${item.id}/done`, { method: 'POST', body: { done: !item.done } })
    reload()
  }

  /* Drag across empty time → Block time; a click → New meeting (§6.5, Board 6 C, D). */
  const [ghost, setGhost] = useState<{ personId: string; date: string; start: number; end: number } | null>(null)
  const startEmpty = (personId: string, day: string, minutes: number, event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    const target = event.currentTarget
    const from = Math.floor(minutes / 15) * 15
    const y0 = event.clientY
    const x0 = event.clientX
    let moved = false
    let picked = { start: from, end: from + 15 }
    /* A finger scrolls; it drags a block of time only after a 300 ms press (§10.1). */
    const hold = event.pointerType === 'touch' ? holdToDrag(event) : null
    const move = (next: PointerEvent) => {
      if (hold && !hold.armed()) return
      const dy = next.clientY - y0
      if (!moved && Math.abs(dy) < 5) return
      moved = true
      const at = Math.round((minutes + dy / scale) / 15) * 15
      picked = { start: Math.min(from, at), end: Math.max(from + 15, at) }
      setGhost({ personId, date: day, ...picked })
    }
    const up = (ended?: PointerEvent) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      const scrolled = ended?.type === 'pointercancel' || (hold?.cancelled() ?? false)
      hold?.end()
      setGhost(null)
      if (scrolled) return
      const name = answer?.people.find((p) => p.id === personId)?.name ?? null
      const rect = target.getBoundingClientRect()
      /* Beside the column, never over the slot that was picked (Board 6 D, Board 3). */
      const anchor = moved ? pointAnchor(rect.right, y0, rect.left, x0)
        : { getBoundingClientRect: () => new DOMRect(rect.left, y0, rect.width, 1) }
      if (moved) setAction({ kind: 'blocktime', personId, personName: name, date: day, ...picked, anchor })
      else setAction({ kind: 'slot', personId, personName: name, date: day, minutes: from, anchor })
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  /*
   * Dragging a stop or a time block (§6.5, Board 6 B): it moves, its drive and
   * the next stop's re-compute live. It goes to another day or another
   * person's column as well as another time (Aaron, Oct 23), wherever the
   * pointer is; a press on its top or bottom edge resizes it instead.
   */
  const [moving, setMoving] = useState<{
    item: CalendarItem; personId: string; target: DropTarget; start: number
    drives: CalendarDrive[] | null; runsInto: string | null
  } | null>(null)
  const [resizing, setResizing] = useState<{ key: string; start: number; end: number } | null>(null)
  const [asking, setAsking] = useState<{
    item: CalendarItem; from: number; to: number; words: string; undo: () => Promise<unknown>
  } | null>(null)
  const suppressClick = useRef(false)
  const swallowClick = () => {
    suppressClick.current = true
    window.setTimeout(() => { suppressClick.current = false }, 0)
  }

  const startMove = (
    item: CalendarItem, personId: string, event: React.PointerEvent<HTMLElement>,
    resolve: (key: string) => DropTarget | null,
  ) => {
    if (event.button !== 0 || !answer) return
    const block = event.currentTarget.getBoundingClientRect()
    const part = pressPart(event.clientY, block.top, block.bottom)
    if (part !== 'move') { startResize(item, part, event); return }
    const y0 = event.clientY
    const x0 = event.clientX
    /* Minutes from the block's start to the press, so the block does not jump under the pointer. */
    const grab = (y0 - block.top) / scale
    const duration = item.end - item.start
    const origin: DropTarget = { date: item.date, personId }
    let moved = false
    let target = origin
    let start = item.start
    let timer: number | null = null
    let last: { drives: CalendarDrive[]; runsInto: string | null } | null = null
    const hold = event.pointerType === 'touch' ? holdToDrag(event) : null
    const ask = (to: DropTarget, minutes: number) => {
      if (timer) window.clearTimeout(timer)
      if (item.kind === 'block') return
      timer = window.setTimeout(() => {
        const at = zonedInstant(to.date, minutes, answer.timeZone).toISOString()
        const elsewhere = to.date !== origin.date || to.personId !== origin.personId
          ? `&fromDate=${origin.date}&fromPerson=${origin.personId}` : ''
        void activityRequest<{ drives: CalendarDrive[]; runsInto: string | null }>(
          `/activity/drives?people=${to.personId}&date=${to.date}&moving=${item.key}&to=${encodeURIComponent(at)}${elsewhere}`)
          .then((value) => {
            last = value
            setMoving((current) => (current && current.item.key === item.key
              && current.target.date === to.date && current.target.personId === to.personId
              ? { ...current, drives: value.drives, runsInto: value.runsInto } : current))
          })
          .catch(() => {})
      }, 150)
    }
    const move = (next: PointerEvent) => {
      if (hold && !hold.armed()) return
      if (!moved && Math.abs(next.clientY - y0) < 5 && Math.abs(next.clientX - x0) < 5) return
      moved = true
      const column = columnAt(next.clientX, next.clientY)
      const to = (column && resolve(column.key)) ?? target
      const top = column ? column.top : block.top - (item.start - hours.start * 60) * scale
      start = movedStart((next.clientY - top) / scale - grab + hours.start * 60, duration)
      const changed = to.date !== target.date || to.personId !== target.personId
      target = to
      if (changed) last = null
      setMoving((current) => ({
        item, personId, target: to, start,
        drives: changed ? null : current?.drives ?? null, runsInto: changed ? null : current?.runsInto ?? null,
      }))
      ask(to, start)
    }
    const up = (ended?: PointerEvent) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      if (timer) window.clearTimeout(timer)
      const held = hold ? hold.armed() && !hold.cancelled() : false
      hold?.end()
      if (!moved) {
        /* A finger held still and lifted is the iPad's right-click (note 1.7). */
        if (held && ended?.type === 'pointerup') { swallowClick(); openMenu(item, { x: x0, y: y0 }) }
        return
      }
      swallowClick()
      const sameSpot = target.date === origin.date && target.personId === origin.personId && start === item.start
      const why = sameSpot ? null : dropRefusal(item, origin, target)
      if (sameSpot || why) {
        setMoving(null)
        if (why) setNotice(why)
        return
      }
      const place: Place = { date: target.date, start, minutes: null,
        personId: target.personId !== origin.personId ? target.personId : null }
      const sameDay = target.date === origin.date && target.personId === origin.personId
      const done = last as { drives: CalendarDrive[]; runsInto: string | null } | null
      const commit = (repeat: 'one' | 'all' | null) => {
        void writeMove(item, place, repeat)
          .then(() => {
            setMoving(null)
            /* Every one is a change to the series, not a stop running into the next: nothing to undo here. */
            if (sameDay && done?.runsInto && repeat !== 'all') {
              const back: Place = { date: origin.date, start: item.start, minutes: null, personId: null }
              setAsking({ item: { ...item, start, end: start + duration }, from: item.start, to: start, words: done.runsInto,
                undo: () => writeMove(item, back, repeat) })
            }
            reload()
            offerTell(item)
          })
          .catch((cause: unknown) => {
            setMoving(null)
            setNotice(sentenceOf(cause, `${item.title} couldn’t be moved.`))
            reload()
          })
      }
      /* A repeat asks first, held where it was dropped until it is answered (small Q3). */
      if (asksWhich(item)) {
        setWhich({
          item, verb: 'move', at: { x: ended?.clientX ?? x0, y: ended?.clientY ?? y0 },
          to: { date: target.date, start, minutes: duration },
          onPick: (choice) => { setWhich(null); commit(choice) },
          onCancel: () => { setWhich(null); setMoving(null) },
        })
        return
      }
      commit(null)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  /* Resizing (Outlook): the bottom edge moves the end, the top edge the start; 15 minutes at least. */
  const startResize = (item: CalendarItem, edge: 'start' | 'end', event: React.PointerEvent<HTMLElement>) => {
    if (!answer) return
    event.preventDefault()
    const y0 = event.clientY
    let moved = false
    let start = item.start
    let end = item.end
    const move = (next: PointerEvent) => {
      if (!moved && Math.abs(next.clientY - y0) < 3) return
      moved = true
      const span = resized(item, edge, (next.clientY - y0) / scale)
      start = span.start
      end = span.end
      setResizing({ key: item.key, start, end })
    }
    const up = (ended?: PointerEvent) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      if (!moved) return
      swallowClick()
      if (start === item.start && end === item.end) { setResizing(null); return }
      const commit = (repeat: 'one' | 'all' | null) => {
        void writeMove(item, { date: item.date, start, minutes: end - start, personId: null }, repeat)
          .then(() => { setResizing(null); reload(); offerTell(item) })
          .catch((cause: unknown) => {
            setResizing(null)
            setNotice(sentenceOf(cause, `${item.title} couldn’t be changed.`))
            reload()
          })
      }
      if (asksWhich(item)) {
        setWhich({
          item, verb: 'resize', at: { x: ended?.clientX ?? 0, y: ended?.clientY ?? y0 },
          to: { date: item.date, start, minutes: end - start },
          onPick: (choice) => { setWhich(null); commit(choice) },
          onCancel: () => { setWhich(null); setResizing(null) },
        })
        return
      }
      commit(null)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  const undoMove = async () => {
    if (!asking) return
    try {
      await asking.undo()
    } catch (cause) {
      setNotice(sentenceOf(cause, `${asking.item.title} couldn’t be put back.`))
    }
    setAsking(null)
    reload()
  }

  /* A double-click opens the thing itself, as Outlook does: a site visit's booking, a meeting's sheet. */
  const openItem = (item: CalendarItem) => {
    setAction(null)
    if (item.kind === 'appointment' || item.kind === 'meeting') openItemSheet(item)
  }

  /* Delete or Backspace on a focused item offers to remove it, by the paths its own card uses. */
  const [removing, setRemoving] = useState<{ item: CalendarItem; rect: DOMRect } | null>(null)
  const offerRemove = (item: CalendarItem, event: React.KeyboardEvent<HTMLElement>) => {
    /* Shift+F10 and the context-menu key are the keyboard's right-click (note 1.7). */
    if (isMenuKey(event)) {
      event.preventDefault()
      openMenu(item, menuAtElement(event.currentTarget))
      return
    }
    if (event.key !== 'Delete' && event.key !== 'Backspace') return
    event.preventDefault()
    setAction(null)
    setRemoving({ item, rect: event.currentTarget.getBoundingClientRect() })
  }
  const remove = async (item: CalendarItem, which: 'one' | 'all') => {
    try {
      if (item.kind === 'block') {
        await activityRequest(`/activity/blocks/${item.id}/remove`, { method: 'POST',
          body: { occurrence: which === 'one' ? occurrenceOf(item) : null } })
      } else if (which === 'one' && item.repeatRule) {
        /* Just this one: its day taken out of the series (small Q3). */
        await activityRequest(`/activity/occurrences/${item.id}/remove`, { method: 'POST', body: { occurrence: occurrenceOf(item) } })
      } else {
        await activityRequest(`/activities/${item.id}`, { method: 'DELETE' })
      }
    } catch (cause) {
      setNotice(sentenceOf(cause, `${item.title} couldn’t be removed.`))
    }
    setRemoving(null)
    reload()
  }

  /* ← → step a day (and ↑ ↓ a week in Month), as Outlook's arrows do, wherever nothing else wants the keys. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (action || removing || menu || which || moveCard || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')
        || KEYED_ROLES.some((role) => target?.closest(`[role="${role}"]`))) return
      const next = arrowDay(view, date, event.key)
      if (!next) return
      event.preventDefault()
      setDate(next)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  /** One column's things, placed (§6.2). `resolve` says what a column under the pointer is, for a drag. */
  const placeItems = (
    day: ActivityDay | undefined, start: number, edge: string | undefined, personId: string | undefined,
    resolve: (key: string) => DropTarget | null,
  ) => {
    if (!day) return null
    const owner = personId ?? answer?.people[0]?.id ?? ''
    /* A stop being dragged leaves the column it came from and is drawn in the one under the pointer. */
    const fromHere = moving && moving.personId === owner && moving.item.date === day.date
    const toHere = moving && moving.target.personId === owner && moving.target.date === day.date
    let items = day.items.filter((item) => !item.allDay && visible(item) && !(fromHere && item.key === moving.item.key))
    if (toHere && moving) {
      const length = moving.item.end - moving.item.start
      items = [...items, { ...moving.item, date: day.date, start: moving.start, end: moving.start + length }]
    }
    if (resizing) items = items.map((item) => (item.key === resizing.key ? { ...item, start: resizing.start, end: resizing.end } : item))
    const drives = toHere && moving?.drives ? moving.drives : day.drives
    const tasks = items.filter((item) => item.kind === 'task')
    const rest = lanesOf(items.filter((item) => item.kind !== 'task'))
    const driveOf = (key: string) => (show.drives ? drives.find((d) => d.key === key) ?? null : null)
    const mine = ghost && ghost.personId === owner && ghost.date === day.date ? ghost : null
    /* While *New meeting for Luis* is open its hour stays drawn, dashed, in his column (Board 3). */
    const slot = action?.kind === 'slot' && action.personId === owner && action.date === day.date ? action : null
    const slotFor = slot ? answer?.people.find((p) => p.id === owner)?.firstName ?? null : null
    return (
      <>
        {rest.map((item) => {
          const width = `calc((100% - 6px) * ${item.span} / ${item.lanes})`
          const style = { top: (item.start - start * 60) * scale, left: `calc(3px + (100% - 6px) * ${item.lane} / ${item.lanes})`, width }
          const dragging = Boolean(toHere && moving && item.key === moving.item.key)
          const press = item.editable ? (event: React.PointerEvent<HTMLElement>) => startMove(item, owner, event, resolve) : undefined
          if (item.kind === 'block') {
            return <TimeBlock key={item.key} item={item} scale={scale}
              style={dragging ? { ...style, outline: '2px dashed rgb(var(--color-accent))', zIndex: 4 } : style}
              onPointerDown={press ?? ((event) => holdForMenu(event, (at) => { swallowClick(); openMenu(item, at) }))}
              resizable={Boolean(press)} onKeyDown={(event) => offerRemove(item, event)} onContextMenu={rightClick(item)}
              onClick={(event) => { if (!suppressClick.current) setAction({ kind: 'block', item, anchor: event.currentTarget }) }} />
          }
          return (
            <StopBlock key={item.key} item={item} drive={driveOf(item.key)} edge={edge} scale={scale}
              style={dragging ? { ...style, background: 'rgb(var(--color-accent-soft))', border: '2px dashed rgb(var(--color-accent))', zIndex: 4 } : style}
              moving={dragging}
              onPointerDown={press ?? ((event) => holdForMenu(event, (at) => { swallowClick(); openMenu(item, at) }))}
              resizable={Boolean(press)}
              onKeyDown={(event) => offerRemove(item, event)}
              onContextMenu={rightClick(item)}
              onDoubleClick={() => openItem(item)}
              onClick={(event) => { if (!suppressClick.current) setAction({ kind: 'stop', item, anchor: event.currentTarget }) }} />
          )
        })}
        {tasks.map((item) => (
          <TaskChip key={item.key} item={item} style={{ top: (item.start - start * 60) * scale, left: 3, right: 3 }}
            onTick={() => void tick(item)} onClick={() => { if (!suppressClick.current) setAction({ kind: 'task', item }) }}
            onKeyDown={(event) => offerRemove(item, event)} onContextMenu={rightClick(item)}
            onPointerDown={(event) => holdForMenu(event, (at) => { swallowClick(); openMenu(item, at) })} />
        ))}
        {mine && (
          <div className="pointer-events-none absolute rounded-[7px] border-2 border-dashed border-accent bg-accent-soft"
            style={{ top: (mine.start - start * 60) * scale, height: (mine.end - mine.start) * scale, left: 3, right: 3, zIndex: 4 }} />
        )}
        {slot && <NewGhost start={slot.minutes} end={slot.minutes + 60} top={(slot.minutes - start * 60) * scale} scale={scale}
          title={owner === me.user.id || !slotFor ? 'New meeting' : `New meeting for ${slotFor}`} />}
        {asking && asking.item.date === day.date && (answer?.people.length === 1 || owner === asking.item.people[0]?.id) && (
          <div className="absolute z-[7] rounded-lg border border-danger-ring bg-surface p-2 text-[11.5px] shadow-popover"
            style={{ top: (asking.item.end - start * 60) * scale + 2, left: 3, right: 3 }}>
            <span className="block text-danger">{asking.words}</span>
            <span className="mt-1.5 flex gap-1.5">
              <button type="button" className="h-7 rounded-md border border-line bg-surface px-2.5 font-semibold" onClick={() => setAsking(null)}>Keep</button>
              <button type="button" className="h-7 rounded-md border border-line bg-surface px-2.5 font-semibold" onClick={() => void undoMove()}>Undo</button>
            </span>
          </div>
        )}
      </>
    )
  }

  const allDayOf = (day: ActivityDay | undefined, firstName: string | null) => {
    if (!day) return null
    const chips: ReactNode[] = []
    if (!day.working && day.offWords) chips.push(<OffChip key="off">{firstName ? day.offWords : 'Off'}</OffChip>)
    for (const item of day.items) {
      if (item.allDay && show.blocks) chips.push(<OffChip key={item.key}>Time off</OffChip>)
    }
    return chips
  }

  /* Several people side by side over a week: a row each (§6.4.1, Board 4). */
  const weekRows = several && mode === 'side' && view !== 'day' && view !== 'month'
  const pastOf = (day: string): GridColumn['past'] => (day < today ? 'all' : day === today ? 'until-now' : 'none')

  let body: ReactNode = null
  if (!answer) {
    body = <div className="rounded-xl border border-line bg-surface p-6 text-[13px] text-text-muted">{error ?? 'Loading…'}</div>
  } else if (view === 'month') {
    body = <MonthGrid answer={answer} date={date} today={today} onOpenDay={(day) => { setDate(day); setView('day') }} />
  } else if (weekRows) {
    body = <WeekRows answer={answer} days={days} today={today} date={date}
      onOpenDay={(day) => { setDate(day); setView('day') }} />
  } else if (combined) {
    body = (
      <CombinedGrid answer={answer} days={days} today={today} date={view === 'day' ? null : date} hours={hours} now={now} layout={layout}
        openAt={openAt} scrollTo={scrollTo} moving={moving} resizing={resizing}
        onItem={(item, anchor) => {
          if (suppressClick.current) return
          setAction(item.kind === 'task' ? { kind: 'task', item } : { kind: item.kind === 'block' ? 'block' : 'stop', item, anchor })
        }}
        /* Combined: a column is a day, and a block stays its person's. */
        onPress={(item, owner, event) => startMove(item, owner, event,
          (key) => (days.includes(key) ? { date: key, personId: owner } : null))}
        onOpen={openItem} onKey={offerRemove} onMenu={(item, at) => openMenu(item, at)}
        onFree={(day, from, to) => {
          /* Everyone shown, and the viewer when they can be booked (§6.4.2, Board 20 A). */
          const self = directory?.people.find((p) => p.id === me.user.id)
          const going = self && self.book !== false ? [me.user.id, ...people.filter((id) => id !== me.user.id)] : people
          openActivityNew({ kind: 'meeting', prefill: { date: day, start: from, minutes: to - from, people: going } })
        }}
        visible={visible} />
    )
  } else if (several && view === 'day') {
    const columns: GridColumn[] = answer.people.map((person) => {
      const day = person.days[0]
      return {
        key: person.id,
        head: (
          /* Board 3 (and 12 B): 26 px initials 7 px from the name, 12.5 over 10.5. */
          <div className="flex items-center gap-[7px] px-3 leading-[normal]">
            <Face person={person} size={26} />
            <span className="flex min-w-0 flex-col">
              <b className="truncate text-[12.5px]">{person.name}</b>
              <span className="truncate text-[10.5px] text-text-muted">
                {person.workDays} · {person.startsFrom.startsWith('home') ? `from ${person.startsFrom}` : person.startsFrom}
              </span>
            </span>
          </div>
        ),
        allDay: allDayOf(day, person.firstName),
        past: pastOf(date),
        today: date === today,
        off: day ? !day.working : false,
        body: placeItems(day, hours.start, person.colour, person.id,
          /* Side by side: a column is a person on this day. */
          (key) => (answer.people.some((p) => p.id === key) ? { date, personId: key } : null)),
        onEmpty: (minutes, event) => startEmpty(person.id, date, minutes, event),
      }
    })
    body = <TimeGrid columns={columns} startHour={hours.start} endHour={hours.end} now={now}
      scale={scale} axisWidth={metrics.axis} allDayWhenEmpty={!tablet} fill initialMinutes={openAt} scrollTo={scrollTo}
      headHeight={tablet ? undefined : 50}
      /* An iPad held upright fits three 240 px columns and scrolls the rest (§10.1). */
      columnWidth={layout === 'portrait' || columns.length > 4 ? metrics.personColumn : undefined} />
  } else {
    const person = answer.people[0]
    const columns: GridColumn[] = days.map((day) => {
      const own = person?.days.find((d) => d.date === day)
      const weather = weatherOf(day)
      return {
        key: day,
        head: <DayHead date={day} today={today} selected={view !== 'day' && day === date}
          weather={view === 'day' ? null : weather ? <Forecast weather={weather} /> : undefined} />,
        allDay: allDayOf(own, person?.firstName ?? null),
        past: pastOf(day),
        today: day === today,
        off: own ? !own.working : false,
        /* One person: a column is a day. */
        body: placeItems(own, hours.start, undefined, person?.id,
          (key) => (person && days.includes(key) ? { date: key, personId: person.id } : null)),
        onEmpty: person ? (minutes, event) => startEmpty(person.id, day, minutes, event) : undefined,
      }
    })
    body = <TimeGrid columns={columns} startHour={hours.start} endHour={hours.end} now={now}
      scale={scale} axisWidth={metrics.axis} allDayWhenEmpty={!tablet} fill initialMinutes={openAt} scrollTo={scrollTo}
      /* Board 12 A: one day on an iPad on its side has a 44 px head. */
      headHeight={layout === 'landscape' && view === 'day' ? 44 : 52} />
  }

  /* The key only where there is free time to find (Board 18 B has none). */
  const freeCount = combined && answer?.dates.some((d) => (d.free ?? []).length > 0) ? people.length : null
  const views: ActivityView[] = layout === 'portrait' ? ['day', 'week', 'month'] : ['day', 'work_week', 'week', 'month']

  return (
    <div className="flex min-h-0 flex-1 items-stretch">
      {/* The side panel is the desktop's; on an iPad its parts are behind the people button (§10.1). */}
      {/* Board 4: Week rows take the whole width — a row per person needs it. */}
      {state.calendar.panel && layout === 'desktop' && !weekRows && (
        <SidePanel date={date} shown={days} today={today} toBook={toBook} onDate={setDate}
          onAdd={() => setAction({ kind: 'choose' })}
          onToBookDragStart={(card, event) => event.dataTransfer.setData('application/x-rowted-tobook', JSON.stringify(card))} />
      )}
      {/* Board 12 A: 30 px between the day and the stop panel beside it. */}
      <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col gap-3 py-3.5', tablet ? 'px-4' : 'px-[18px]',
        layout === 'landscape' && action?.kind === 'stop' && 'pr-[30px]')}>
        <CalendarToolbar height={tablet ? 40 : 32}
          /* The iPad's toolbar never wraps: one day reads *Thu, Oct 22* there (Board 12). */
          range={tablet && view === 'day' ? `${weekdayShort(date)}, ${monthDay(date)}` : rangeLabel(view, date)} view={view} views={views} onView={setView}
          onToday={() => { setDate(today); setScrollTo({ minutes: now, nonce: Date.now() }) }}
          onStep={(direction) => setDate(stepView(view, date, direction))}
          several={several ? mode : null} onSeveral={(value) => change({ calendar: { several: value } })}
          forecast={dayWeather} freeCount={freeCount}
        />
        {body}
      </div>
      <CalendarOverlays action={action} onClose={() => setAction(null)} onChanged={reload} me={me.user.id}
        timeZone={answer?.timeZone ?? 'America/New_York'}
        placement={layout === 'landscape' ? 'dock' : layout === 'portrait' ? 'sheet' : 'float'}
        onMenu={(item, at, card) => openMenu(item, at, card, true)} onReschedule={reschedule} />
      {menu && <CalendarMenu items={menuItems} at={menu.at} label={`${menu.item.title} — what to do`} onClose={closeMenu} />}
      {which && <WhichQuestion item={which.item} verb={which.verb} at={which.at} to={which.to}
        onPick={which.onPick} onCancel={which.onCancel} />}
      {moveCard && (
        <MoveCard item={moveCard.item} mode={moveCard.mode} at={moveCard.at}
          onClose={() => setMoveCard(null)}
          onSave={async (place) => {
            await writeMove(moveCard.item, { ...place, personId: null }, moveCard.mode === 'plain' ? null : moveCard.mode)
            reload()
            offerTell(moveCard.item)
          }} />
      )}
      {tell && (
        <TellPrompt card={tell} canSend={canSend} onClose={() => setTell(null)}
          onMessage={(channel) => { setTell(null); messageAbout(tell, channel, true) }}
          onConfirm={() => { setTell(null); if (tell.job) openNewRequest({ book: { jobId: tell.job.id, appointmentId: tell.id } }) }} />
      )}
      {removing && <RemovePrompt item={removing.item} rect={removing.rect} onRemove={(which) => void remove(removing.item, which)}
        onOpenJob={(jobId) => { setRemoving(null); navigate(`/jobs/${jobId}`) }}
        onClose={() => setRemoving(null)} />}
      {notice && (
        <div role="status" className={cn('fixed left-1/2 z-[80] -translate-x-1/2 rounded-[10px] border border-line bg-surface px-4 py-2.5 text-[13px] font-medium text-text shadow-popover',
          tell ? 'bottom-[calc(96px+var(--activity-foot,0px))]' : 'bottom-[calc(24px+var(--activity-foot,0px))]')}>
          {notice}
        </div>
      )}
    </div>
  )
}

/**
 * *Remove …?* beside an item that Delete or Backspace (or the menu's
 * *Remove…*) was pressed on: a time block by its card's own choices (*Just
 * this one · Every week*), a repeating meeting or task the same way (*Just
 * this one · Every one* — Aaron, Oct 24, small Q3), a one-off outright, and a
 * site visit from its job, where it is booked.
 */
function RemovePrompt({ item, rect, onRemove, onOpenJob, onClose }: {
  item: CalendarItem; rect: DOMRect; onRemove: (which: 'one' | 'all') => void
  onOpenJob: (jobId: string) => void; onClose: () => void
}) {
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    box.current?.querySelector<HTMLButtonElement>('button')?.focus()
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const left = Math.min(window.innerWidth - 300, Math.max(8, rect.right + 8))
  const top = Math.min(window.innerHeight - 120, Math.max(8, rect.top))
  const BTN = 'inline-flex h-8 items-center rounded-lg border border-line bg-surface px-3 text-[12.5px] font-semibold'
  return (
    <div ref={box} role="dialog" aria-label={`Remove ${item.title}`}
      className="fixed z-[70] w-[284px] rounded-xl border border-line bg-surface p-3 text-[12.5px] shadow-popover"
      style={{ left, top }}>
      {!item.editable ? (
        <>
          <p className="m-0 text-text-body">You can’t remove {item.title}.</p>
          <div className="mt-2.5 flex justify-end"><button type="button" className={BTN} onClick={onClose}>OK</button></div>
        </>
      ) : item.kind === 'appointment' ? (
        <>
          <p className="m-0 text-text-body">A site visit is cancelled from its job.</p>
          <div className="mt-2.5 flex justify-end gap-1.5">
            <button type="button" className={BTN} onClick={onClose}>Close</button>
            {item.jobId && <button type="button" className={BTN} onClick={() => onOpenJob(item.jobId!)}>Open the job</button>}
          </div>
        </>
      ) : (
        <>
          <b className="block text-[13px]">Remove {item.title}?</b>
          <div className="mt-2.5 flex flex-wrap justify-end gap-1.5">
            <button type="button" className={BTN} onClick={onClose}>Cancel</button>
            {item.repeatRule ? (
              <>
                <button type="button" className={BTN} onClick={() => onRemove('one')}>Just this one</button>
                <button type="button" className={cn(BTN, 'text-danger')} onClick={() => onRemove('all')}>
                  {item.kind === 'block' ? 'Every week' : 'Every one'}
                </button>
              </>
            ) : (
              <button type="button" className={cn(BTN, 'text-danger')} onClick={() => onRemove('all')}>Remove</button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Combined (§6.4.2, Board 18): one grid with days as columns; a block's edge
 * and fill are its person's, overlaps share the width in lanes, a shared
 * meeting is drawn once with a striped edge, and *Free for all* is hatched.
 */
function CombinedGrid({
  answer, days, today, date, hours, now, onItem, onFree, visible, layout, openAt, scrollTo, moving, resizing, onPress, onOpen, onKey,
  onMenu,
}: {
  layout: Exclude<ActivityLayout, 'phone'>
  answer: CalendarAnswer
  days: string[]
  today: string
  /** The day the arrows are on, ringed in its head (null in Day). */
  date: string | null
  hours: { start: number; end: number }
  now: number
  openAt: number
  scrollTo: { minutes: number; nonce: number } | null
  /** A block being dragged or resized, drawn where it is going. */
  moving: { item: CalendarItem; target: { date: string }; start: number } | null
  resizing: { key: string; start: number; end: number } | null
  onPress: (item: CalendarItem, owner: string, event: React.PointerEvent<HTMLElement>) => void
  onOpen: (item: CalendarItem) => void
  onKey: (item: CalendarItem, event: React.KeyboardEvent<HTMLElement>) => void
  /** A right-click, or a finger held on a task: the item's menu (note 1.7). */
  onMenu: (item: CalendarItem, at: MenuAt) => void
  onItem: (item: CalendarItem, anchor: HTMLElement) => void
  /** Free time dragged or clicked: the span for *New meeting* (a click: an hour from its 15-min mark). */
  onFree: (date: string, from: number, to: number) => void
  visible: (item: CalendarItem) => boolean
}) {
  const metrics = GRID_METRICS[layout]
  const scale = metrics.scale
  const colours = new Map(answer.people.map((p) => [p.id, p.colour]))
  const initials = new Map(answer.people.map((p) => [p.id, p]))
  /* A lane under 130 px uses the narrow layout (§6.4.2), so the grid's width is measured. */
  const box = useRef<HTMLDivElement>(null)
  const [gridWidth, setGridWidth] = useState(0)
  useEffect(() => {
    const element = box.current
    if (!element) return undefined
    const measure = () => setGridWidth(element.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const watcher = new ResizeObserver(measure)
    watcher.observe(element)
    return () => watcher.disconnect()
  }, [])
  const columnWidth = gridWidth > 0 ? (gridWidth - metrics.axis - 2) / Math.max(1, days.length) : 0
  /* Board 18 A: dragging in free time draws *New meeting · 4 people* as it goes. */
  const [drag, setDrag] = useState<{ date: string; start: number; end: number } | null>(null)
  const startFree = (day: string, span: { start: number; end: number }, event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    const body = event.currentTarget.parentElement!.getBoundingClientRect()
    const minuteAt = (y: number) => hours.start * 60 + (y - body.top) / scale
    const from = Math.min(span.end - 15, Math.max(span.start, Math.floor(minuteAt(event.clientY) / 15) * 15))
    const y0 = event.clientY
    let moved = false
    let picked = { start: from, end: from + 60 }
    setDrag({ date: day, ...picked })
    const move = (next: PointerEvent) => {
      if (!moved && Math.abs(next.clientY - y0) < 5) return
      moved = true
      const at = Math.round(minuteAt(next.clientY) / 15) * 15
      picked = at >= from + 15 ? { start: from, end: Math.min(span.end, at) }
        : { start: Math.max(span.start, Math.min(at, from)), end: from + 15 }
      setDrag({ date: day, ...picked })
    }
    const up = (ended: PointerEvent) => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      setDrag(null)
      if (ended.type === 'pointercancel') return
      onFree(day, picked.start, picked.end)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }
  /* The block being dragged leaves its day and is drawn, alone in its lane, on the day under the pointer. */
  const placed = (answer.combined?.items ?? []).map((item) => {
    if (moving && item.key === moving.item.key) {
      return { ...item, date: moving.target.date, start: moving.start, end: moving.start + (item.end - item.start), lane: 0, lanes: 1, span: 1 }
    }
    if (resizing && item.key === resizing.key) return { ...item, start: resizing.start, end: resizing.end }
    return item
  })
  const columns: GridColumn[] = days.map((day) => {
    const items = placed.filter((item) => item.date === day && visible(item))
    const free = answer.dates.find((d) => d.date === day)?.free ?? []
    const offPeople = answer.people.filter((p) => p.days.find((d) => d.date === day && !d.working))
    const weather = answer.dates.find((d) => d.date === day)?.weather
    return {
      key: day,
      head: <DayHead date={day} today={today} selected={day === date} weather={weather ? <Forecast weather={weather} /> : undefined} />,
      allDay: offPeople.map((p) => {
        const tone = toneOf(p.colour)
        /* The person's tone pair, as their initials wear it (Board 18 A: *Maya off*). */
        return (
          <span key={p.id} className="flex h-5 items-center truncate rounded-[5px] px-[7px] text-[11px] font-semibold"
            style={{ background: tone.tint, color: tone.text }}>{p.firstName} off</span>
        )
      }),
      past: day < today ? 'all' : day === today ? 'until-now' : 'none',
      today: day === today,
      off: false,
      body: (
        <>
          {free.map((span) => (
            <button key={span.from} type="button" onPointerDown={(event) => startFree(day, span, event)}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onFree(day, span.start, span.start + 60) } }}
              className="absolute flex flex-col items-start justify-start overflow-hidden whitespace-nowrap rounded-[7px] border border-dashed border-accent-line px-[7px] py-1 text-left text-[10.5px] font-bold text-accent"
              style={{ top: (span.start - hours.start * 60) * scale, height: (span.end - span.start) * scale - BLOCK_GAP, left: 3, right: 3,
                background: 'repeating-linear-gradient(-45deg,rgb(var(--color-accent-soft)),rgb(var(--color-accent-soft)) 6px,rgb(234 245 238) 6px,rgb(234 245 238) 12px)' }}>
              <span>
                Free for all {answer.people.length}
                {span.end - span.start >= 60 && ` · ${clockSpan(span.start, span.end)}`}
              </span>
            </button>
          ))}
          {drag && drag.date === day && (
            <NewGhost start={drag.start} end={drag.end} top={(drag.start - hours.start * 60) * scale} scale={scale}
              title={`New meeting · ${answer.people.length} people`} />
          )}
          {items.map((item: CombinedItem) => {
            const owner = item.showing[0] ?? item.people[0]?.id
            const tone = personFill(colours.get(owner ?? '') ?? null)
            const left = `calc(3px + (100% - 6px) * ${item.lane} / ${item.lanes})`
            const width = `calc((100% - 6px) * ${item.span} / ${item.lanes})`
            const style = { top: (item.start - hours.start * 60) * scale, left, width }
            const person = owner ? initials.get(owner) : undefined
            /* Board 18: 15 px initials in a 1.5 px white ring. */
            const lead = person ? (
              <span className="inline-flex rounded-full border-[1.5px] border-white"><Face person={person} size={15} /></span>
            ) : undefined
            /* Board 18: a timed task and a time block carry no initials — the edge is the person. */
            const menuAt = (event: React.MouseEvent<HTMLElement>) => { event.preventDefault(); onMenu(item, { x: event.clientX, y: event.clientY }) }
            if (item.kind === 'task') {
              return <TaskChip key={item.key} item={item} style={style} onClick={(event) => onItem(item, event.currentTarget)}
                onKeyDown={(event) => onKey(item, event)} onContextMenu={menuAt}
                onPointerDown={(event) => holdForMenu(event, (at) => onMenu(item, at))} />
            }
            const press = item.editable && owner ? (event: React.PointerEvent<HTMLElement>) => onPress(item, owner, event) : undefined
            const shared = item.showing.length > 1
            const lane = columnWidth * item.span / item.lanes
            /* Board 18 A: a time block in a narrow lane reads its start only (*2:15 Office*). */
            if (item.kind === 'block') {
              return <TimeBlock key={item.key} item={item} edge={tone.edge} style={style} scale={scale}
                narrow={lane > 0 && lane < 130} onClick={(event) => onItem(item, event.currentTarget)}
                onPointerDown={press} onKeyDown={(event) => onKey(item, event)} onContextMenu={menuAt} />
            }
            return (
              <StopBlock key={item.key} item={item} edge={tone.edge} scale={scale}
                fill={shared ? undefined : tone.fill}
                stripes={shared ? item.showing.map((id) => personFill(colours.get(id) ?? null).edge) : undefined}
                narrow={lane > 0 ? lane < 130 : item.lanes / item.span > 1.6}
                /* Board 18: tags stay in the peek; over a week the job line does too (A), one day has room (B). */
                bare={days.length > 1} tags={false}
                lead={shared ? undefined : lead} style={style}
                onPointerDown={press} onDoubleClick={() => onOpen(item)} onKeyDown={(event) => onKey(item, event)}
                onContextMenu={menuAt} onClick={(event) => onItem(item, event.currentTarget)} />
            )
          })}
          {(answer.combined?.more ?? []).filter((m) => m.date === day).map((m) => (
            <span key={`${m.date}-${m.start}`} className="absolute right-[3px] text-[11px] font-semibold text-accent"
              style={{ top: (m.start - hours.start * 60) * scale, width: 'calc((100% - 6px) / 4)' }}>+ {m.count} more</span>
          ))}
        </>
      ),
    }
  })
  return (
    <div ref={box} className="flex min-h-0 flex-1 flex-col">
      <TimeGrid columns={columns} startHour={hours.start} endHour={hours.end} now={now}
        scale={scale} axisWidth={metrics.axis} allDayWhenEmpty={layout === 'desktop'}
        fill initialMinutes={openAt} scrollTo={scrollTo} />
    </div>
  )
}

/** A dashed hour that is about to be a meeting (Boards 3, 6 D, 18 A). */
function NewGhost({ start, end, top, scale, title }: { start: number; end: number; top: number; scale: number; title: string }) {
  return (
    <div className="pointer-events-none absolute overflow-hidden rounded-[7px] border-2 border-dashed border-accent bg-accent-soft py-1 pl-2.5 pr-[7px] leading-[normal]"
      style={{ top, height: (end - start) * scale - BLOCK_GAP, left: 3, right: 3, zIndex: 4 }}>
      <span className="tnum block whitespace-nowrap text-[10.5px] font-semibold text-text-muted">{clockSpan(start, end)}</span>
      <b className="mt-px block truncate text-[12px] leading-[1.25]">{title}</b>
    </div>
  )
}
